import * as React from 'react'
//
import '../../utils/fonts'

import { Image, Text, View } from '@react-pdf/renderer'

import logo from '../../assets/logos/medlogix'
import { ErrorBoundary } from '../../error-boundary'
import { useCountry } from '../../hooks/useCountry'
import { TDocument } from '../../types'
import { default as Utils, default as utils } from '../../utils'
import styles from './Header.style'

interface HeaderProps {
	doc?: TDocument
}

export const Header: React.FC<HeaderProps> = ({ doc }) => {
	const { getCountryByCode } = useCountry()
	const organizationLogo = utils?.getOrganizationLogo(doc?.document)
	const organization = Utils?.getOrganization(doc?.document)
	const customer = Utils?.getCustomer(doc?.document)
	const pharmacy = doc?.document?.pharmacy
	const settingsHealthcare = doc?.documentSettings?.settingsHealthcare || {}
	const customerCountry = getCountryByCode(customer?.country)

	let documentPrefix = ''
	if (doc?.documentTemplate === `Prescription`) {
		documentPrefix = settingsHealthcare?.prescriptionPrefix || ''
	}

	if (doc?.documentTemplate === `Letter`) {
		const documentType = doc?.document?.documentType
		if (documentType === `general`) {
			documentPrefix = settingsHealthcare?.generalPrefix || ''
		} else if (documentType === `sickNote`) {
			documentPrefix = settingsHealthcare?.sickNotePrefix || ''
		} else if (documentType === `referralLetter`) {
			documentPrefix = settingsHealthcare?.referralLetterPrefix || ''
		} else if (documentType === `familyResponsibility`) {
			documentPrefix = settingsHealthcare?.familyResponsibilityPrefix || ''
		} else if (documentType === `testResults`) {
			documentPrefix = settingsHealthcare?.testResultsPrefix || ''
		} else if (documentType === `prescription`) {
			documentPrefix = settingsHealthcare?.prescriptionPrefix || ''
		} else if (documentType === `dischargeSummary`) {
			documentPrefix = settingsHealthcare?.dischargeSummaryPrefix || ''
		} else if (documentType === `urgentCare`) {
			documentPrefix = settingsHealthcare?.urgentCarePrefix || ''
		} else {
			documentPrefix = settingsHealthcare?.prescriptionPrefix || ''
		}
	}

	return (
		<>
			<View style={[styles.headerOrganization]}>
				<ErrorBoundary message={`Error rendering organization logo`}>
					<View style={styles.headerOrganizationLeft}>
						<Image
							style={styles.image}
							src={organizationLogo ? organizationLogo : logo?.logo()}
						/>
					</View>
				</ErrorBoundary>
				<View style={styles.headerOrganizationCenter}>
					{organization?.name && (
						<Text
							style={[
								styles.text,
								{ fontFamily: 'Inter', fontSize: 16, fontWeight: 'bold' },
							]}
						>
							{organization?.name}
						</Text>
					)}
					{organization?.addressBilling && (
						<Text style={[styles.text, { marginBottom: '2mm' }]}>
							{Utils.formatAddress({
								address: organization?.addressBilling?.address,
								addressOptional: organization?.addressBilling?.addressOptional,
								city: organization?.addressBilling?.city,
								postCode: organization?.addressBilling?.postCode,
								country: organization?.addressBilling?.name?.common,
							})}
						</Text>
					)}
					{organization?.addressBilling?.address !==
						organization?.addressShipping?.address && (
						<Text style={[styles.text, { marginBottom: '2mm' }]}>
							{Utils.formatAddress({
								address: organization?.addressShipping?.address,
								addressOptional: organization?.addressShipping?.addressOptional,
								city: organization?.addressShipping?.city,
								postCode: organization?.addressShipping?.postCode,
								country: organization?.addressShipping?.name?.common,
							})}
						</Text>
					)}
				</View>
				<View style={styles.headerOrganizationRight}>
					{organization?.phoneNumberString && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Phone: </Text>
							</View>
							<Text style={styles.text}>{organization?.phoneNumberString}</Text>
						</View>
					)}
					{organization?.faxNumberString && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={[styles.textBold]}>Fax: </Text>
							</View>
							<Text style={styles.text}>{organization?.faxNumberString}</Text>
						</View>
					)}
					{organization?.email && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Email: </Text>
							</View>
							<Text style={styles.text}>{organization?.email}</Text>
						</View>
					)}
					{organization?.url && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Website: </Text>
							</View>
							<Text style={styles.text}>{organization?.url}</Text>
						</View>
					)}
					<View style={{ minHeight: '2mm' }} />
					{organization?.taxID && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Tax ID #: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>{organization?.taxID}</Text>
							</View>
						</View>
					)}
					{organization?.vatID && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>VAT Number #: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>{organization?.vatID}</Text>
							</View>
						</View>
					)}
					{organization?.registrationNumber && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Reg Number #: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>{organization?.registrationNumber}</Text>
							</View>
						</View>
					)}
					{organization?.practiceNumber && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Practice Number #: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>{organization?.practiceNumber}</Text>
							</View>
						</View>
					)}
				</View>
			</View>
			<View style={styles.headerDocTitle}>
				<View style={styles.headerOrganizationLeft}></View>
				<Text
					style={{ fontSize: 15, fontWeight: `bold` }}
				>{`${documentPrefix || ''}${doc?.document?.objectNumber || ''}`}</Text>
			</View>
			<View style={[styles.headerOrganization]}>
				<View style={[styles.headerOrganizationLeft, { paddingTop: '1mm' }]}>
					{doc?.verify && (
						<>
							<Image style={styles.verify} src={doc?.verify} />
							<Text
								style={{
									fontSize: 7.2,
									paddingLeft: '2.1mm',
									fontFamily: 'Mono',
									letterSpacing: '1px',
								}}
							>
								Verify
							</Text>
						</>
					)}
				</View>
				<View style={styles.headerOrganizationCenter}>
					<Text
						style={[
							styles.text,
							{
								fontSize: 10,
								fontFamily: 'Inter',
								fontWeight: 'bold',
								marginBottom: '2mm',
							},
						]}
					>
						FOR PATIENT:
					</Text>
					<Text
						style={[
							styles.text,
							{ fontSize: 8, fontFamily: 'Inter', fontWeight: 'bold' },
						]}
					>
						{`${customer?.firstName} ${customer?.lastName}`}
					</Text>
					{customer?.idNumber && (
						<Text style={[styles.text]}>ID Number: {customer?.idNumber}</Text>
					)}
					{customer?.address && (
						<Text
							style={[styles.text, { marginBottom: '2mm' }]}
						>{`${Utils.formatAddress({
							address: customer?.address,
							addressOptional: customer?.addressOptional,
							city: customer?.city,
							postCode: customer?.postCode,
							country: customerCountry?.name?.common,
						})}`}</Text>
					)}
					{pharmacy && pharmacy?.name && (
						<>
							<Text style={[styles.text, { fontSize: 8, fontFamily: 'Inter' }]}>
								Pharmacy
							</Text>
							<Text style={[styles.text]}>{pharmacy?.name}</Text>
							<Text style={[styles.text]}>{pharmacy?.address}</Text>
						</>
					)}
				</View>
				<View style={styles.headerOrganizationRight}>
					<Text
						style={[
							styles.text,
							{ fontSize: 10, fontFamily: 'Inter', marginBottom: '2mm' },
						]}
					>
						DETAILS:
					</Text>
					{doc?.document?.status && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Status: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{doc?.document?.status === 'inSession'
										? 'Complete'
										: doc?.document?.status?.toUpperCase()}
								</Text>
							</View>
						</View>
					)}
					{doc?.document?.prescription?.icdCode && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>ICD Code: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{doc?.document?.prescription?.icdCode?.toUpperCase()}
								</Text>
							</View>
						</View>
					)}
					{doc?.document?.bearer?.firstName && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Issued by: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{`${doc?.document?.bearer?.salutation || ''} ${
										doc?.document?.bearer?.firstName || ''
									} ${doc?.document?.bearer?.lastName || ''}`}
								</Text>
							</View>
						</View>
					)}
					{doc?.document?.bearer?.practitionerNumber && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Pract. Number: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{`${doc?.document?.bearer?.practitionerNumber || ''} `}
								</Text>
							</View>
						</View>
					)}
					{doc?.document?.bearer?.specialization && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Specialization: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{`${doc?.document?.bearer?.specialization || ''} `}
								</Text>
							</View>
						</View>
					)}
					{doc?.document?.dateFrom && (
						<View style={styles?.detailsRow}>
							<View style={styles?.detailsRowTitle}>
								<Text style={styles.textBold}>Consultation Date: </Text>
							</View>
							<View style={styles?.detail}>
								<Text style={styles.text}>
									{Utils.dateFormat(
										doc?.document?.dateFrom?.iso || doc?.document?.dateFrom,
										'short'
									)}
								</Text>
							</View>
						</View>
					)}
				</View>
			</View>
		</>
	)
}
